// ChangeLogPage — Nhật ký thay đổi dữ liệu NGHIỆP VỤ (audit viewer hợp nhất 5 module
// MAST/PROJ/FINA/HRPY/ASST). KHÁC với "Nhật ký hành động" (AuditPage) của Auth vốn chỉ
// ghi login/đổi quyền. Trang này đọc TC/HD/DM/NS/TS *_AuditLog gộp qua API /api/audit/*.
//
// Bảng AntD server-paging (offset/limit + TotalCount), filter bar (khoảng ngày mặc định 30
// ngày, Module, Hành động, keyword debounce), Drawer chi tiết so sánh Trước → Sau (highlight
// trường đổi). Nhãn Module/Hành động/Đối tượng map sang tiếng Việt (thường ngữ — không thuật
// ngữ kế toán), i18n vi/en qua namespace changelog.*.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card, Space, Button, Select, Input, Table, Tag, Drawer, Descriptions,
  Typography, Collapse, Tooltip, DatePicker, Empty,
} from 'antd';
import { ReloadOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useGridPageSize } from '../../hooks/useGridPageSize.js';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Màu Tag theo module (bám palette AntD preset).
const MODULE_COLOR = {
  MAST: 'blue', PROJ: 'geekblue', FINA: 'green', HRPY: 'gold', ASST: 'cyan',
};
// Màu Tag theo nhóm hành động.
const ACTION_COLOR = {
  Create: 'green', Update: 'blue', Deactivate: 'orange', Reactivate: 'green',
  Delete: 'red', HardDelete: 'red', Status: 'purple', StatusChange: 'purple',
  Upsert: 'blue', Upload: 'cyan',
};

// Định dạng thời gian VN: cộng +7 vào mốc UTC rồi đọc getter UTC → ra đúng giờ treo tường
// Asia/Ho_Chi_Minh, độc lập múi giờ máy client.
function formatVN(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const vn = new Date(d.getTime() + 7 * 3600 * 1000);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(vn.getUTCDate())}/${p(vn.getUTCMonth() + 1)}/${vn.getUTCFullYear()} `
    + `${p(vn.getUTCHours())}:${p(vn.getUTCMinutes())}:${p(vn.getUTCSeconds())}`;
}

// Trường kỹ thuật (timestamp + Guid dài) → đẩy xuống cuối bảng so sánh cho gọn.
const TECH_FIELD = /^(CreatedAt|UpdatedAt|CreatedAtUTC|UpdatedAtUTC|CreatedBy|UpdatedBy|RowVersion)$|Guid$|GUID$/;

function safeParse(json) {
  if (json == null || json === '') return null;
  if (typeof json === 'object') return json;
  try { return JSON.parse(json); } catch { return null; }
}

export function ChangeLogPage({ axios }) {
  const { t, i18n } = useTranslation();

  const moduleLabel = useCallback((m) => (m ? t(`changelog.module.${m}`, m) : ''), [t]);
  const actionLabel = useCallback((a) => (a ? t(`changelog.action.${a}`, a) : ''), [t]);
  const resourceLabel = useCallback(
    (r) => (r ? t(`changelog.resource.${r}`, r) : ''),
    [t],
  );
  const fieldLabel = useCallback((f) => t(`changelog.field.${f}`, f), [t]);

  // ---- Filter state ----
  const [range, setRange] = useState(() => [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')]);
  const [module, setModule] = useState('');
  const [action, setAction] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');

  // Debounce keyword.
  useEffect(() => {
    const id = setTimeout(() => setKeyword(keywordInput.trim()), 400);
    return () => clearTimeout(id);
  }, [keywordInput]);

  // ---- Paging ----
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useGridPageSize('change-log', 25);
  useEffect(() => { setPage(1); }, [range, module, action, keyword, pageSize]);

  // ---- Facets ----
  const [facets, setFacets] = useState({ Modules: [], Actions: [] });
  useEffect(() => {
    let alive = true;
    axios.get('/api/audit/facets')
      .then((r) => { if (alive) setFacets(r.data?.Data ?? { Modules: [], Actions: [] }); })
      .catch(() => { /* facets optional */ });
    return () => { alive = false; };
  }, [axios]);

  // ---- Data ----
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = {
      offset: (page - 1) * pageSize,
      limit: pageSize,
      sortDir: 'desc',
    };
    if (range?.[0]) params.dateFrom = range[0].format('YYYY-MM-DD');
    if (range?.[1]) params.dateTo = range[1].format('YYYY-MM-DD');
    if (module) params.module = module;
    if (action) params.action = action;
    if (keyword) params.keyword = keyword;

    axios.get('/api/audit/log', { params })
      .then((r) => {
        if (!alive) return;
        const data = r.data?.Data ?? {};
        setRows(data.Items ?? []);
        setTotal(data.TotalCount ?? (data.Items?.length ?? 0));
      })
      .catch(() => { if (alive) { setRows([]); setTotal(0); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [axios, page, pageSize, range, module, action, keyword, reloadTick]);

  // ---- Detail drawer ----
  const [detail, setDetail] = useState(null);
  const openDetail = (row) => setDetail(row);

  const moduleOptions = useMemo(() => [
    { value: '', label: t('changelog.filter.allModules') },
    ...(facets.Modules ?? []).map((m) => ({ value: m, label: moduleLabel(m) })),
  ], [facets.Modules, moduleLabel, t]);

  const actionOptions = useMemo(() => [
    { value: '', label: t('changelog.filter.allActions') },
    ...(facets.Actions ?? []).map((a) => ({ value: a, label: actionLabel(a) })),
  ], [facets.Actions, actionLabel, t]);

  const columns = useMemo(() => [
    {
      title: t('changelog.col.time'), dataIndex: 'CreatedAtUTC', key: 'time', width: 160,
      render: (v) => formatVN(v),
    },
    {
      title: t('changelog.col.actor'), key: 'actor', width: 170,
      render: (_, r) => {
        const name = r.ActorFullName || r.ActorUsername || t('changelog.system');
        return r.ActorIPAddress
          ? <Tooltip title={`IP: ${r.ActorIPAddress}`}><span>{name}</span></Tooltip>
          : <span>{name}</span>;
      },
    },
    {
      title: t('changelog.col.module'), dataIndex: 'Module', key: 'module', width: 130,
      render: (v) => (v ? <Tag color={MODULE_COLOR[v] ?? 'default'}>{moduleLabel(v)}</Tag> : null),
    },
    {
      title: t('changelog.col.action'), dataIndex: 'Action', key: 'action', width: 130,
      render: (v) => (v ? <Tag color={ACTION_COLOR[v] ?? 'default'}>{actionLabel(v)}</Tag> : null),
    },
    {
      title: t('changelog.col.target'), key: 'target',
      render: (_, r) => {
        if (!r.TargetResourceType && r.TargetResourceID == null) return '';
        const type = resourceLabel(r.TargetResourceType);
        return r.TargetResourceID != null ? `${type} #${r.TargetResourceID}` : type;
      },
    },
    {
      title: t('changelog.col.detail'), key: 'detail', width: 90, align: 'center',
      render: (_, r) => (
        <Button size="small" type="link" icon={<EyeOutlined />} onClick={() => openDetail(r)}>
          {t('changelog.view')}
        </Button>
      ),
    },
  ], [t, moduleLabel, actionLabel, resourceLabel]);

  return (
    <Card
      title={t('changelog.title')}
      styles={{ body: { display: 'flex', flexDirection: 'column', gap: 12 } }}
    >
      <Space wrap size={[8, 8]}>
        <RangePicker
          value={range}
          onChange={(v) => setRange(v && v[0] && v[1] ? v : null)}
          format="DD/MM/YYYY"
          allowClear
        />
        <Select
          style={{ width: 180 }} value={module} onChange={setModule} options={moduleOptions}
        />
        <Select
          style={{ width: 180 }} value={action} onChange={setAction} options={actionOptions}
        />
        <Input
          allowClear prefix={<SearchOutlined />} style={{ width: 240 }}
          placeholder={t('changelog.filter.keywordPh')}
          value={keywordInput} onChange={(e) => setKeywordInput(e.target.value)}
        />
        <Button icon={<ReloadOutlined />} onClick={() => setReloadTick((x) => x + 1)} loading={loading}>
          {t('button.refresh')}
        </Button>
      </Space>

      <Table
        size="small"
        bordered
        rowKey="AuditID"
        columns={columns}
        dataSource={rows}
        loading={loading}
        onRow={(r) => ({ onDoubleClick: () => openDetail(r) })}
        locale={{ emptyText: <Empty description={t('changelog.empty')} /> }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50', '100', '200'],
          showTotal: (n) => t('changelog.totalRows', { count: n }),
          onChange: (p, ps) => { setPage(p); if (ps !== pageSize) setPageSize(ps); },
        }}
        scroll={{ y: 'calc(100vh - 320px)' }}
      />

      <ChangeDetailDrawer
        record={detail}
        onClose={() => setDetail(null)}
        t={t}
        lang={i18n.language}
        moduleLabel={moduleLabel}
        actionLabel={actionLabel}
        resourceLabel={resourceLabel}
        fieldLabel={fieldLabel}
      />
    </Card>
  );
}

// ---- Drawer: so sánh Trước → Sau ----
function renderValue(v, t) {
  if (v == null || v === '') return <Text type="secondary">—</Text>;
  if (typeof v === 'boolean') return v ? t('changelog.boolTrue') : t('changelog.boolFalse');
  if (typeof v === 'object') return <Text code style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(v)}</Text>;
  return String(v);
}

function ChangeDetailDrawer({ record, onClose, t, lang, moduleLabel, actionLabel, resourceLabel, fieldLabel }) {
  const before = safeParse(record?.SnapshotBefore);
  const after = safeParse(record?.SnapshotAfter);
  const metadata = safeParse(record?.Metadata);

  // Chế độ hiển thị: chỉ Sau (tạo mới) / chỉ Trước (xoá-ngưng) / cả hai (sửa).
  const mode = before && after ? 'diff' : (after ? 'after' : (before ? 'before' : 'none'));

  const diffRows = useMemo(() => {
    const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
    const list = [...keys].map((k) => {
      const bv = before?.[k];
      const av = after?.[k];
      const changed = mode === 'diff' && JSON.stringify(bv ?? null) !== JSON.stringify(av ?? null);
      return { key: k, before: bv, after: av, changed, tech: TECH_FIELD.test(k) };
    });
    // Sắp: trường đổi trước → thường → kỹ thuật cuối.
    return list.sort((a, b) => {
      if (a.changed !== b.changed) return a.changed ? -1 : 1;
      if (a.tech !== b.tech) return a.tech ? 1 : -1;
      return a.key.localeCompare(b.key);
    });
  }, [before, after, mode]);

  const showBeforeCol = mode === 'diff' || mode === 'before';
  const showAfterCol = mode === 'diff' || mode === 'after';

  const cols = [
    { title: t('changelog.detail.field'), dataIndex: 'key', width: 200, render: (k) => fieldLabel(k) },
  ];
  if (showBeforeCol) {
    cols.push({
      title: t('changelog.detail.before'), dataIndex: 'before',
      render: (v, row) => ({
        props: { style: row.changed ? { background: 'rgba(250, 219, 20, 0.18)' } : undefined },
        children: renderValue(v, t),
      }),
    });
  }
  if (showAfterCol) {
    cols.push({
      title: t('changelog.detail.after'), dataIndex: 'after',
      render: (v, row) => ({
        props: { style: row.changed ? { background: 'rgba(250, 219, 20, 0.18)' } : undefined },
        children: renderValue(v, t),
      }),
    });
  }

  return (
    <Drawer
      title={t('changelog.detail.title')}
      width={720}
      open={!!record}
      onClose={onClose}
      destroyOnClose
    >
      {record && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('changelog.col.time')}>{formatVN(record.CreatedAtUTC)}</Descriptions.Item>
            <Descriptions.Item label={t('changelog.col.actor')}>
              {record.ActorFullName || record.ActorUsername || t('changelog.system')}
              {record.ActorIPAddress ? <Text type="secondary"> ({record.ActorIPAddress})</Text> : null}
            </Descriptions.Item>
            <Descriptions.Item label={t('changelog.col.module')}>
              {record.Module ? <Tag color={MODULE_COLOR[record.Module] ?? 'default'}>{moduleLabel(record.Module)}</Tag> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('changelog.col.action')}>
              {record.Action ? <Tag color={ACTION_COLOR[record.Action] ?? 'default'}>{actionLabel(record.Action)}</Tag> : '—'}
              {record.EventType ? <Text type="secondary"> · {record.EventType}</Text> : null}
            </Descriptions.Item>
            <Descriptions.Item label={t('changelog.col.target')}>
              {record.TargetResourceType
                ? `${resourceLabel(record.TargetResourceType)}${record.TargetResourceID != null ? ` #${record.TargetResourceID}` : ''}`
                : '—'}
            </Descriptions.Item>
            {record.Reason ? (
              <Descriptions.Item label={t('changelog.detail.reason')}>{record.Reason}</Descriptions.Item>
            ) : null}
          </Descriptions>

          {mode === 'none' ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('changelog.detail.noSnapshot')} />
          ) : (
            <Table
              size="small"
              bordered
              rowKey="key"
              columns={cols}
              dataSource={diffRows}
              pagination={false}
            />
          )}

          {metadata ? (
            <Collapse
              size="small"
              items={[{
                key: 'meta',
                label: t('changelog.detail.metadata'),
                children: (
                  <Text code style={{ whiteSpace: 'pre-wrap', display: 'block' }}>
                    {JSON.stringify(metadata, null, 2)}
                  </Text>
                ),
              }]}
            />
          ) : null}
        </Space>
      )}
    </Drawer>
  );
}
